import { executeDeleteSearchHistory, executeFetchHistorySuggest, executeSuggestSearchByKeyword } from "../logic/core";
import Logger from "../util/logger";
import { toInt } from "../util/text";

/**
 * イベントハンドラーの引数にコンテキストを紐づけたコールバック関数を生成します。
 *
 * @template {JQuery.Event | Event} T イベント型
 * @template U イベントハンドラーの戻り値
 * @param {(context: SuggestContext, event: T) => U} eventHandler イベントハンドラー
 * @param {SuggestContext} context コンテキスト
 * @returns {(event: T) => U}
 */
export function createEventHandlerAdapter(eventHandler, context) {
  /**
   * @template {JQuery.Event | Event} T
   * @this {HTMLElement}
   * @param {T} event
   */
  return function (event) {
    return eventHandler.call(this, context, event);
  }
}

/**
 * カテゴリ検索画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @param {string} categoryId カテゴリコード
 * @param {string} [searchKeyword] 検索キーワード
 * @returns {string}
 */
export function buildCategorySearchPageUrl(baseUrl, categoryId, searchKeyword) {
  const url = `${baseUrl}s/${categoryId}/`;
  return searchKeyword ? `${url}?q=${encodeURIComponent(searchKeyword)}` : url;
}

/**
 * キーワード検索画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @param {string} keyword 検索キーワード
 * @returns {string}
 */
export function buildKeywordSearchPageUrl(baseUrl, keyword) {
  return `${baseUrl}keyword/?q=${encodeURIComponent(keyword)}`;
}

/**
 * タグ一覧画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @returns {string}
 */
export function buildTagListPageUrl(baseUrl,) {
  return `${baseUrl}tag/list/`;
}

/**
 * タグコード検索画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @param {string} tagCode タグコード
 * @returns {string}
 */
export function buildTagCodeSearchPageUrl(baseUrl, tagCode) {
  return `${baseUrl}tag/${tagCode}/`;
}

/**
 * メーカー検索画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @param {string} makerId メーカーコード
 * @returns {string}
 */
export function buildMakerSearchPageUrl(baseUrl, makerId) {
  return `${baseUrl}maker/${makerId}/`;
}

/**
 * 商品詳細画面のURLを構築する。
 *
 * @param {string} baseUrl ベースURL
 * @param {string} productNo 品番
 * @param {ProductDetailPageUrlOptions} [params] オプションパラメータ
 * @returns {string}
 */
export function buildProductDetailPageUrl(baseUrl, productNo, params) {
  const url = `${baseUrl}d/${productNo}/`;
  if (!params) {
    return url;
  }
  return url + '?' + Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

/**
 * 品番/型番/商品名サジェストから画面表示用商品名へ構築します。
 *
 * @param {ProductSuggestItem} suggestItem サジェスト
 * @param {boolean} isProductNameSuggest 商品名サジェストフラグ
 * @return {string}
 */
export function buildDisplayProductName(suggestItem, isProductNameSuggest) {
  let stopProductText = '';
  if (suggestItem.direct_use_stop_flg == '1') {
    stopProductText = ' [取扱停止] ';
  } else if (suggestItem.direct_order_stop_flg == '1') {
    stopProductText = ' [受注停止] ';
  }
  if (isProductNameSuggest) {
    return `${stopProductText}${suggestItem.direct_product_name}`;
  } else {
    return `${suggestItem.word} [${stopProductText}${suggestItem.direct_product_name}]`;
  }
}

/**
 * ユニサジェストAPIが返却したカテゴリIDまたはカテゴリ名を分割する。
 *
 * @param {string} directCategoryIdOrName ユニサジェストAPIが返却したカテゴリIDまたはカテゴリ名
 * @return {string[]}
 */
export function splitCategory(directCategoryIdOrName) {
  return directCategoryIdOrName.split(' > ');
}

/**
 * 分割したカテゴリIDまたはカテゴリ名を再度結合する。
 *
 * @param {string[]} categoryList カテゴリリスト
 * @returns {string}
 */
export function joinCategory(categoryList) {
  return categoryList.join(' > ');
}

/**
 * サジェストの検索を行い、結果を表示する。
 *
 * @param {string} keyword 検索キーワード
 * @param {SuggestContext} context サジェストコンテキスト
 * @returns {Promise<void>}
 */
export async function showSuggestListByKeyword(keyword, context) {
  const suggestRoot = findWordSuggestRoot(context.config);
  const historySuggetRoot = findHistorySuggestRoot(context.config);
  const countString = suggestRoot.attr('data-count');
  const lastCount = countString ? toInt(countString) : 0;
  const lastKeyword = suggestRoot.attr('data-keyword');
  if (lastKeyword === keyword) {
    if (lastCount) {
      historySuggetRoot.hide();
      suggestRoot.show();
    }
    return;
  }

  const logger = new Logger(`サジェスト表示（キーワード：${keyword}）`);
  logger.debug('検索開始');
  const response = await executeSuggestSearchByKeyword(keyword, context.config);
  if (!response) {
    // TODO: katono SHOP版不具合No.180の暫定対応でエラー表示を一時的に抑止
    // alert('エラーが発生しました。');
    return;
  }

  logger.debug('レンダリング開始');
  const count = response.response?.numFound ?? 0;
  suggestRoot.attr('data-count', count)
    .attr('data-keyword', keyword);
  context.uiHandler.renderWordSuggests(context, suggestRoot, keyword, response);
  historySuggetRoot.hide();
  if (count) {
    suggestRoot.show();
  }
  logger.debug('レンダリング終了');
}

/**
 * 履歴サジェストを読み込み、結果を表示する。
 *
 * @param {SuggestContext} context サジェスト設定
 * @returns {Promise<void>}
 */
export async function showSuggestListByHistory(context) {
  const suggestRoot = findHistorySuggestRoot(context.config);
  const wordSuggestRoot = findWordSuggestRoot(context.config);
  const countString = suggestRoot.attr('data-count');
  const lastCount = countString ? toInt(countString) : 0;
  if (suggestRoot.attr('data-searched')) {
    if (lastCount) {
      wordSuggestRoot.hide();
      suggestRoot.show();
    }
    return;
  }

  const logger = new Logger('履歴サジェスト表示');
  logger.debug('取得開始');
  const response = await executeFetchHistorySuggest(context.config);
  if (!response) {
    // TODO: katono SHOP版不具合No.180の暫定対応でエラー表示を一時的に抑止
    // alert('エラーが発生しました。');
    return;
  }

  logger.debug('レンダリング開始');
  const count = response.response?.numFound ?? 0;
  suggestRoot.attr('data-searched', 1)
    .attr('data-count', count);
  context.uiHandler.renderHistorySuggests(context, suggestRoot, response);
  wordSuggestRoot.hide();
  if (count) {
    suggestRoot.show();
    return;
  }
  logger.debug('レンダリング終了');
}

/**
 * 履歴サジェストの「履歴を全て削除」選択時に履歴を全て削除する
 *
 * @this {HTMLElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.ClickEvent} event クリックイベント
 */
export async function onClickDeleteHistory(context, event) {
  const isYes = window.confirm('履歴をすべて削除しますか？');
  if (!isYes) {
    return;
  }

  const res = await executeDeleteSearchHistory(context.config);
  if (!res) {
    // TODO: katono SHOP版不具合No.180の暫定対応でエラー表示を一時的に抑止
    // alert('エラーが発生しました。');
    return;
  }

  const suggestRoot = findHistorySuggestRoot(context.config);
  suggestRoot.attr('data-count', 0)
    .hide();
}

/**
 * ワードサジェストのルート要素を検索する。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {JQuery<HTMLElement>}
 */
export function findWordSuggestRoot(config) {
  return $(config.wordSuggestRoot);
}

/**
 * 履歴サジェストのルート要素を検索する。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {JQuery<HTMLElement>}
 */
export function findHistorySuggestRoot(config) {
  return $(config.historySuggestRoot);
}

/**
 * 現在表示されているサジェストのルート要素を検索する。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {JQuery<HTMLElement>}
 */
export function findVisibleSuggestRoot(config) {
  const searchSelector = `${config.wordSuggestRoot},${config.historySuggestRoot}`;
  const result = $(searchSelector).filter((index, element) => $(element).is(':visible'));
  return result
}

/**
 * 検索テキストボックスキー入力時にサジェストの選択またはキーワード検索を行う。
 *
 * @this {HTMLInputElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.KeyDownEvent} event キー入力イベント
 */
function onKeyDownSearchBox(context, event) {
  const suggest = findVisibleSuggestRoot(context.config);
  if (!suggest.length) {
    return;
  }

  switch (event.key) {
    case 'ArrowUp':
    case 'ArrowDown': {
      event.preventDefault();
      const nextActiveSuggest = context.uiHandler.moveActiveSuggest(context, suggest, event.key === 'ArrowUp' ? -1 : 1);
      const keyword = context.uiHandler.resolveDisplayKeyword(context, nextActiveSuggest);
      $(this).val(keyword);
      break;
    }
    case 'Enter':
      const activeSuggest = suggest.find('.active');
      if (activeSuggest.length) {
        event.preventDefault();
        context.uiHandler.selectSuggest(context, activeSuggest);
      }
      break;
  }
}

/** @type {null|number} サジェスト検索時の遅延タイマーID */
let timerIdByInputSearchBox = null;

/**
 * 検索テキストボックス入力時にサジェストの表示/更新/非表示を制御する。
 *
 * @this {HTMLInputElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.KeyboardEventBase} event 入力イベント
 */
function onInputSearchBox(context, event) {
  if (timerIdByInputSearchBox) {
    window.clearTimeout(timerIdByInputSearchBox);
  }

  // APIへの過剰リクエストを防止するため、入力完了後にAPI呼び出しを行う
  let handler;
  const keyword = $(event.currentTarget).val().trim();
  if (keyword) {
    handler = () => showSuggestListByKeyword(keyword, context);
  } else {
    handler = () => showSuggestListByHistory(context);
  }
  timerIdByInputSearchBox = window.setTimeout(
    () => {
      timerIdByInputSearchBox = null;
      handler();
    },
    context.config.delay,
  );
}

/**
 * 検索テキストボックスにフォーカスが当たったときにサジェストを表示する。
 *
 * @this {HTMLInputElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.FocusEvent} event フォーカスイベント
 */
function onFocusSearchBox(context, event) {
  const keyword = $(event.currentTarget).val().trim();
  if (keyword) {
    showSuggestListByKeyword(keyword, context);
  } else {
    showSuggestListByHistory(context);
  }
}

/**
 * 検索テキストボックスからフォーカスが外れたときにサジェストを非表示にする。
 *
 * @this {HTMLInputElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.BlurEvent} event ブラーイベント
 */
function onBlurSearchBox(context, event) {
  // NOTE: サジェスト選択時にブラーイベントが先に発火してしまうため、非表示を遅延させる
  const suggest = findVisibleSuggestRoot(context.config);
  if (suggest.length) {
    setTimeout(() => {
      suggest.hide()
        .find('.active')
        .removeClass('active');
    }, 250);
  }
}

/**
 * サジェスト表示を行うためのイベントハンドラーを設定する。
 *
 * @param {JQuery<HTMLInputElement>} target 対象要素
 * @param {SuggestContext} context サジェストコンテキスト
 */
export function setupEventHandlers(target, context) {
  // 検索テキストボックスのイベントハンドラー設定
  target.on('keydown', createEventHandlerAdapter(onKeyDownSearchBox, context))
    // @ts-ignore
    .on('input', createEventHandlerAdapter(onInputSearchBox, context))
    .on('focus', createEventHandlerAdapter(onFocusSearchBox, context))
    .on('blur', createEventHandlerAdapter(onBlurSearchBox, context));
}
